/**
 * What each channel tells us about a post, and how "engagements" is counted.
 *
 * One table read by the worker (what to collect, what to leave NULL), the API
 * (what to sum, which platforms have no stats) and the web (a "—" with "X does
 * not report reach" instead of a 0 that looks like a result).
 *
 * A metric a channel does not report is stored as NULL in `post_analytics`,
 * never 0: a 0 reach on X read as "nobody saw it".
 *
 * `views` is the headline number: Meta, X, YouTube, LinkedIn and Pinterest
 * all report views (impressions) since 2025; reach exists on fewer channels.
 * It is stored in the `impressions` column.
 */

/** A per-post number a channel may report. */
export type AnalyticsMetric = 'views' | 'reach' | 'likes' | 'comments' | 'shares' | 'saves' | 'clicks' | 'videoViews';

/**
 * Where a channel's post numbers come from:
 *  - `collector`: the 6-hourly analytics job reads the channel's insights API;
 *  - `feed`: the social-feed sync already reads the post's counts and writes
 *    them as a snapshot (Zetpin, TikTok);
 *  - `none`: the channel has no per-post numbers we can read (Telegram bots,
 *    WhatsApp, Google Business Profile).
 */
export type AnalyticsStatsSource = 'collector' | 'feed' | 'none';

export interface PlatformStats {
  source: AnalyticsStatsSource;
  /** The metrics the channel reports; every other one is NULL, shown as "—". */
  metrics: readonly AnalyticsMetric[];
}

/** Keyed by the Platform enum value (upper case). */
export const PLATFORM_STATS: Readonly<Record<string, PlatformStats>> = {
  INSTAGRAM: { source: 'collector', metrics: ['views', 'reach', 'likes', 'comments', 'shares', 'saves', 'videoViews'] },
  FACEBOOK: { source: 'collector', metrics: ['views', 'reach', 'likes', 'comments', 'shares', 'clicks'] },
  // public_metrics has bookmark_count: X's "save".
  TWITTER: { source: 'collector', metrics: ['views', 'likes', 'comments', 'shares', 'saves', 'clicks'] },
  LINKEDIN: { source: 'collector', metrics: ['views', 'reach', 'likes', 'comments', 'shares', 'clicks'] },
  YOUTUBE: { source: 'collector', metrics: ['views', 'likes', 'comments', 'videoViews'] },
  PINTEREST: { source: 'collector', metrics: ['views', 'saves', 'clicks'] },
  TIKTOK: { source: 'feed', metrics: ['views', 'likes', 'comments', 'shares', 'videoViews'] },
  ZETPIN: { source: 'feed', metrics: ['likes', 'comments'] },
  TELEGRAM: { source: 'none', metrics: [] },
  WHATSAPP: { source: 'none', metrics: [] },
  GOOGLE_BUSINESS: { source: 'none', metrics: [] },
};

/** The channels whose insights API the analytics collector reads (F22: the others are never enqueued). */
export const COLLECTABLE_PLATFORMS: readonly string[] = Object.entries(PLATFORM_STATS)
  .filter(([, s]) => s.source === 'collector')
  .map(([p]) => p);

/** Whether `platform` reports `metric` (false for an unknown platform). */
export function platformReports(platform: string, metric: AnalyticsMetric): boolean {
  return PLATFORM_STATS[platform.toUpperCase()]?.metrics.includes(metric) ?? false;
}

/** Whether a channel has any per-post numbers at all. */
export function platformHasStats(platform: string): boolean {
  return (PLATFORM_STATS[platform.toUpperCase()]?.source ?? 'none') !== 'none';
}

export interface EngagementCounts {
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  saves?: number | null;
}

/**
 * Engagements, defined once: likes + comments + shares + saves. Clicks are
 * shown on their own (a link click is not a reaction to the post). The API's
 * SQL uses the same sum (`ENGAGEMENTS_SQL`), as do the CSV, the heatmap and
 * the weekly email. A metric not reported (null) counts as nothing.
 */
export function engagementsOf(m: EngagementCounts): number {
  return (m.likes ?? 0) + (m.comments ?? 0) + (m.shares ?? 0) + (m.saves ?? 0);
}

/**
 * Engagement rate, defined once: engagements ÷ views, 0–1. null when there
 * are no views to divide by (a channel that reports none, or none yet).
 */
export function engagementRateOf(engagements: number, views: number | null | undefined): number | null {
  if (!views || views <= 0) return null;
  return engagements / views;
}
